import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '../../../../components/alert'
import Button from '../../../../components/tailwind/Button'
import { usePartnerContext } from '../../../../context'
import { useSuggestedCareersQuery } from '../../../../hooks/useSuggestedCareersQuery'
import { useDiscover } from '../../hooks/useDiscover'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence
} from '../../utils'
import CareerBadge from './CareerBadge'
import IsLoadingChooseExample from './IsLoadingChooseExample'
import fallbackCareers from './suggestedCareers'

export default function Step1ChooseExample({ onNext, example, onBack }) {
  const {
    enableAssistant,
    sendMessageToAssistant,
    setAssistantIsTyping,
    setAssistantDetail,
    setAssistantFocus,
    hideAssistant
  } = useDiscover()
  const hasSentAssistantMessage = useRef(false)
  // Send assistant message on mount when loading is done

  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const [selectedCareer, setSelectedCareer] = useState(example)
  const [suggestedCareers, setSuggestedCareers] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)

  const companyName = partner.display_name || partner.name

  const { data, isLoading, isError, error, refetch, isFetching } =
    useSuggestedCareersQuery(companyName)

  useEffect(() => {
    if (isLoading) {
      sendMessageToAssistant({
        id: 'step-1-loading',
        description: t('discover.assistant.create_career.step_1_loading', {
          companyName
        }),
        role: MESSAGE_ROLES.ASSISTANT,
        type: MESSAGE_TYPES.TEXT
      })
    }

    if (!isLoading && !hasSentAssistantMessage.current) {
      hasSentAssistantMessage.current = true
      enableAssistant()

      setAssistantIsTyping(true)
      sendSmartMessageSequence({
        setIsTyping: setAssistantIsTyping,
        callbackFn: () => {
          setTimeout(() => {
            setAssistantFocus(false)
          }, 1500)
        },
        messages: [
          {
            fn: () =>
              sendMessageToAssistant({
                id: 1,
                role: MESSAGE_ROLES.ASSISTANT,
                type: MESSAGE_TYPES.TEXT,
                title: t(
                  'discover.assistant.system_message.choose_example.title'
                ),
                description: t(
                  'discover.assistant.system_message.choose_example.description'
                )
              }),
            textLength:
              t('discover.assistant.system_message.choose_example.title')
                .length +
              t('discover.assistant.system_message.choose_example.description')
                .length
          }
        ]
      })
    }
  }, [
    isLoading,
    t,
    companyName,
    enableAssistant,
    setAssistantDetail,
    setAssistantFocus,
    setAssistantIsTyping,
    sendMessageToAssistant
  ])

  useEffect(() => {
    if (isLoading) return

    let parsed = []
    let msg = null

    try {
      if (data) {
        const level1 = typeof data === 'string' ? JSON.parse(data) : data
        const body = level1?.responseBody?.TEXT?.body
        const level2 = typeof body === 'string' ? JSON.parse(body) : body

        if (level2?.status === 'error') {
          msg =
            level2?.message ||
            t('discover.create_career.step_1.error_default_message')
        } else if (Array.isArray(level2)) {
          parsed = level2
        } else if (Array.isArray(level2?.suggestions)) {
          parsed = level2.suggestions
        }
      }
    } catch (e) {
      console.error('Error parsing suggested careers:', e)
      msg = t('discover.create_career.step_1.error_parsing_message')
    }

    if (
      (isError || msg) &&
      Array.isArray(fallbackCareers) &&
      fallbackCareers.length
    ) {
      setSuggestedCareers(fallbackCareers)
    } else {
      setSuggestedCareers(parsed)
    }

    if (isError) {
      setErrorMessage(
        error?.message ||
          t('discover.create_career.step_1.error_network_message')
      )
    } else {
      setErrorMessage(msg)
    }
  }, [data, isLoading, isError, error, t])

  return (
    <div className='pt-4'>
      {/* <StepHeadline>{t('discover.create_career.step_1.title')}</StepHeadline>
      <StepDescription>
        {t('discover.create_career.step_1.description')}
      </StepDescription> */}
      <div className='flex min-h-80 flex-col items-start rounded-2xl border border-softGrey bg-white p-6'>
        <span className='text-xs font-semibold uppercase text-rockGrey'>
          {t('discover.create_career.step_1.company')}
        </span>
        <div className='mt-2.5 rounded border border-softGrey bg-lightPaleGrey px-2.5 py-1.5 text-xs font-semibold text-darkBlack'>
          {partner.display_name || partner.name}
        </div>
        <div className='mt-6 w-full'>
          {isLoading ? (
            <IsLoadingChooseExample />
          ) : errorMessage ? (
            <div className='mb-4'>
              <Alert
                type='error'
                title={t('discover.create_career.step_1.error_title')}
                description={errorMessage}
              />
              <div className='mt-3'>
                <button
                  type='button'
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className='rounded-md border border-softGrey bg-white px-3 py-1.5 text-xs font-semibold text-darkBlack hover:bg-lightPaleGrey disabled:opacity-50'
                >
                  {isFetching ? t('common.loading') : t('common.retry')}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className='flex flex-row items-center justify-between'>
                <span className='text-xs font-semibold uppercase text-rockGrey'>
                  {t('discover.create_career.step_1.career_title')}
                </span>
                <div className='rounded-full border border-[#FFB62933] bg-lightYellow px-2 py-1 text-xs font-semibold text-darkYellow'>
                  {t('discover.create_career.step_1.career_tip')}
                </div>
              </div>

              <div className='mt-4 flex flex-row flex-wrap gap-2'>
                {suggestedCareers?.map((career) => (
                  <CareerBadge
                    key={career.id}
                    title={career.title}
                    isSelected={selectedCareer?.id === career.id}
                    onClick={() => setSelectedCareer(career)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='mt-6 flex flex-row items-center gap-x-2.5'>
        <Button.SecondaryXL text={t('common.back')} onClick={onBack} />
        <Button.PrimaryXL
          text={t('common.next')}
          onClick={() => {
            // TODO: CLEAR ASSISTANT
            hideAssistant()
            onNext(selectedCareer)
          }}
          disabled={!selectedCareer}
        />
      </div>
    </div>
  )
}

Step1ChooseExample.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  example: PropTypes.object
}
